import { useMemo, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import AgenteContabilidadeShell from "../components/AgenteContabilidadeShell";
import HelpSection from "../components/HelpSection";
import { colors } from "../constants/designTokens";
import { helpSections } from "../data/helpContent";

const SEARCH_RESULT_LIMIT = 8;

const mojibakeMap = [
  ["Ã¡", "a"],
  ["Ã ", "a"],
  ["Ã¢", "a"],
  ["Ã£", "a"],
  ["Ã©", "e"],
  ["Ãª", "e"],
  ["Ã­", "i"],
  ["Ã³", "o"],
  ["Ã´", "o"],
  ["Ãµ", "o"],
  ["Ãº", "u"],
  ["Ã§", "c"],
  ["Ã", "a"],
  ["Ã€", "a"],
  ["Ã‚", "a"],
  ["Ãƒ", "a"],
  ["Ã‰", "e"],
  ["ÃŠ", "e"],
  ["Ã", "i"],
  ["Ã“", "o"],
  ["Ã”", "o"],
  ["Ã•", "o"],
  ["Ãš", "u"],
  ["Ã‡", "c"],
];

function normalizeSearchText(value = "") {
  let text = String(value);
  mojibakeMap.forEach(([from, to]) => {
    text = text.replaceAll(from, to);
  });

  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function addSearchItem(items, section, type, title, text, priority) {
  if (!text) return;

  const rawText = String(text);
  items.push({
    id: `${section.id}-${type}-${items.length}`,
    sectionId: section.id,
    sectionTitle: section.title,
    type,
    title: title || section.title,
    text: rawText,
    searchable: normalizeSearchText(`${section.title} ${title || ""} ${rawText}`),
    priority,
  });
}

function buildHelpSearchIndex(sections) {
  const items = [];

  sections.forEach((section) => {
    addSearchItem(items, section, "Seção", section.title, section.summary, 2);

    section.steps?.forEach((step, index) => {
      addSearchItem(items, section, "Passo a passo", `Passo ${index + 1}`, step, 3);
    });

    section.details?.forEach((detail) => {
      addSearchItem(items, section, "Detalhe", detail.title, detail.text, 3);
    });

    if (section.notice?.text) {
      addSearchItem(items, section, "Aviso", "Observação", section.notice.text, 3);
    }

    section.glossary?.forEach(([term, description]) => {
      addSearchItem(items, section, "Glossário", term, description, 1);
    });

    section.actions?.forEach((action) => {
      addSearchItem(items, section, "Atalho", action.label, action.label, 4);
    });
  });

  return items;
}

function getResultScore(result, normalizedQuery) {
  const normalizedTitle = normalizeSearchText(result.title);
  const normalizedSectionTitle = normalizeSearchText(result.sectionTitle);

  if (normalizedTitle === normalizedQuery) return 0;
  if (result.type === "Glossário" && normalizedTitle.includes(normalizedQuery)) return 1;
  if (normalizedSectionTitle.includes(normalizedQuery)) return 2;
  return result.priority + 3;
}

function searchHelp(query, index) {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [];

  return index
    .filter((item) => item.searchable.includes(normalizedQuery))
    .map((item) => ({
      ...item,
      score: getResultScore(item, normalizedQuery),
    }))
    .sort((a, b) => a.score - b.score || a.sectionTitle.localeCompare(b.sectionTitle))
    .slice(0, SEARCH_RESULT_LIMIT);
}

export default function AjudaPage() {
  const [expandedSections, setExpandedSections] = useState(["primeiros-passos"]);
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const searchIndex = useMemo(() => buildHelpSearchIndex(helpSections), []);
  const searchResults = useMemo(
    () => searchHelp(searchQuery, searchIndex),
    [searchIndex, searchQuery]
  );
  const hasSearch = normalizeSearchText(searchQuery).length > 0;

  function handleSectionChange(sectionId) {
    return (_, isExpanded) => {
      setExpandedSections((current) =>
        isExpanded
          ? [...new Set([...current, sectionId])]
          : current.filter((id) => id !== sectionId)
      );
    };
  }

  function handleIndexClick(sectionId) {
    setExpandedSections((current) => [...new Set([...current, sectionId])]);
    window.requestAnimationFrame(() => {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  return (
    <AgenteContabilidadeShell
      title="Central de Ajuda"
      subtitle="Aprenda a utilizar o Agente de Contabilidade passo a passo"
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "250px minmax(0, 1fr)" },
          gap: 2,
          mt: 2,
          alignItems: "start",
        }}
      >
        <Paper
          component="nav"
          aria-label="Índice da Central de Ajuda"
          elevation={0}
          sx={{
            position: { xs: "static", lg: "sticky" },
            top: { lg: 16 },
            maxHeight: { lg: "calc(100vh - 112px)" },
            overflowY: { lg: "auto" },
            p: 1.5,
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            bgcolor: colors.surface,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.75, mb: 0.5 }}>
            <HelpOutlineRounded sx={{ color: colors.primary, fontSize: 20 }} />
            <Typography sx={{ color: colors.text, fontWeight: 900 }}>
              Neste guia
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: "row", lg: "column" }}
            spacing={0.35}
            sx={{ overflowX: { xs: "auto", lg: "visible" }, pb: { xs: 0.5, lg: 0 } }}
          >
            {helpSections.map((section) => (
              <Button
                key={section.id}
                onClick={() => handleIndexClick(section.id)}
                sx={{
                  minWidth: { xs: "max-content", lg: 0 },
                  justifyContent: "flex-start",
                  px: 1,
                  py: 0.75,
                  borderRadius: 1.5,
                  color: colors.muted,
                  textTransform: "none",
                  textAlign: "left",
                  fontSize: 12.5,
                  fontWeight: 700,
                  "&:hover": {
                    bgcolor: colors.surfaceAlt,
                    color: colors.primary,
                  },
                }}
              >
                {section.title}
              </Button>
            ))}
          </Stack>
        </Paper>

        <Stack spacing={1.8} minWidth={0}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              bgcolor: colors.surfaceAlt,
            }}
          >
            <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 18 }}>
              Comece pela importação e avance no seu ritmo
            </Typography>
            <Typography sx={{ color: colors.muted, mt: 0.6, lineHeight: 1.6, fontSize: 14 }}>
              Este guia acompanha o fluxo real do produto. Use o índice para abrir uma
              seção específica ou pesquise por indicadores, módulos e dúvidas frequentes.
            </Typography>
            {!isDesktop ? (
              <Typography sx={{ color: colors.softMuted, mt: 1, fontSize: 12.5 }}>
                Deslize o índice horizontalmente para consultar todas as seções.
              </Typography>
            ) : null}
            <TextField
              fullWidth
              size="small"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Pesquise por KPI, módulo ou dúvida"
              sx={{ mt: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ color: colors.muted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>

          {hasSearch ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 2.25 },
                border: `1px solid ${colors.border}`,
                borderRadius: 2,
                bgcolor: colors.surface,
              }}
            >
              <Stack spacing={1.25}>
                <Typography sx={{ color: colors.text, fontWeight: 900 }}>
                  Resultados da pesquisa
                </Typography>

                {searchResults.length ? (
                  <Stack spacing={1}>
                    {searchResults.map((result) => (
                      <Box
                        key={result.id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
                          gap: 1,
                          p: 1.35,
                          border: `1px solid ${colors.border}`,
                          borderRadius: 1.5,
                          bgcolor: colors.surfaceAlt,
                        }}
                      >
                        <Box minWidth={0}>
                          <Typography sx={{ color: colors.primary, fontSize: 12, fontWeight: 900 }}>
                            {result.type} - {result.sectionTitle}
                          </Typography>
                          <Typography sx={{ color: colors.text, fontWeight: 900, mt: 0.25 }}>
                            {result.title}
                          </Typography>
                          <Typography sx={{ color: colors.muted, fontSize: 13.5, lineHeight: 1.55, mt: 0.35 }}>
                            {result.text}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={() => handleIndexClick(result.sectionId)}
                          sx={{ textTransform: "none", fontWeight: 800, alignSelf: "center" }}
                        >
                          Abrir seção
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ color: colors.muted, fontSize: 14 }}>
                    Nenhum resultado encontrado para este termo.
                  </Typography>
                )}
              </Stack>
            </Paper>
          ) : null}

          {helpSections.map((section, index) => {
            const previousSection = helpSections[index - 1];
            const previousIsExpanded = previousSection
              ? expandedSections.includes(previousSection.id)
              : false;

            return (
              <Box
                key={section.id}
                sx={{
                  mt: index === 0 ? 0 : previousIsExpanded ? 3 : 0,
                }}
              >
                <HelpSection
                  section={section}
                  expanded={expandedSections.includes(section.id)}
                  onChange={handleSectionChange(section.id)}
                />
              </Box>
            );
          })}
        </Stack>
      </Box>
    </AgenteContabilidadeShell>
  );
}
